import React from 'react';
import {
  CampaignActionItem,
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignDeliveryTarget,
  actionItemStatusLabel,
  deliveryStatusLabel,
  statusLabel,
  stepKindLabel,
  stepStatusLabel,
} from '../../../../../support/campaigns';
import {
  useFetchActionItems,
  useFetchDeliveryStepStatuses,
  useFetchDeliveryTargets,
} from '../../../../../hooks/campaign-hooks';
import { fmtDate, stepStatusDistribution } from '../../../../../support/campaignAnalytics';
import { deliveryPipeline, formatPipelineUsd } from '../../../../../support/campaignLeader';
import { deliveryHealth } from '../../../../../support/campaignHealth';
import { StatusPill } from '../../builder/primitives';
import { toneForStatus } from '../statusTone';
import {
  ChartCard,
  Column,
  HealthNote,
  MasterTable,
  Panel,
  ViewLink,
  dashboardCss as css,
} from '../dashboardParts';

/**
 * One delivery: its step-level status (chart + table), and the two things a delivery ultimately drives
 * — the customers/opportunities it targets and the action items it captured. Selecting a customer row
 * drills to that specific account/opportunity. All three sources are fetched here and deduped by
 * react-query, so a target view opened next reuses them.
 */
const DeliveryView: React.FC<{
  delivery: CampaignDelivery;
  onSelectTarget: (target: CampaignDeliveryTarget) => void;
  targetsByDelivery: Map<number, CampaignDeliveryTarget[]>;
  amountByOpp: Map<string, number>;
}> = ({ delivery, onSelectTarget, targetsByDelivery, amountByOpp }) => {
  const { data: statuses } = useFetchDeliveryStepStatuses(delivery.id);
  const { data: targets } = useFetchDeliveryTargets(delivery.id);
  const { data: actions } = useFetchActionItems(delivery.id);

  const statusList = React.useMemo(() => statuses ?? [], [statuses]);
  const targetList = React.useMemo(() => targets ?? [], [targets]);
  const actionList = React.useMemo(() => actions ?? [], [actions]);

  // Attributed $ on this delivery and per-opportunity, plus its diagnostic health (statuses loaded here).
  const pipelineAmount = React.useMemo(
    () => deliveryPipeline(delivery, targetsByDelivery, amountByOpp),
    [delivery, targetsByDelivery, amountByOpp]
  );
  const health = React.useMemo(
    () => deliveryHealth(delivery, statusList, pipelineAmount),
    [delivery, statusList, pipelineAmount]
  );

  const stepCols: Column<CampaignDeliveryStepStatus>[] = [
    { key: 'step', header: 'Step', render: (s) => s.step_name ?? `Step #${s.step}` },
    { key: 'kind', header: 'Kind', render: (s) => stepKindLabel(s.step_kind) },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <StatusPill tone={toneForStatus(s.status)}>{stepStatusLabel(s.status)}</StatusPill>,
    },
    { key: 'completed', header: 'Completed', render: (s) => fmtDate(s.completed_at) },
  ];

  const targetCols: Column<CampaignDeliveryTarget>[] = [
    { key: 'account', header: 'Account', render: (t) => t.customer_name ?? t.customer ?? '—' },
    { key: 'opp', header: 'Opportunity', render: (t) => t.opportunity ?? '—' },
    {
      key: 'amount',
      header: 'Attributed $',
      numeric: true,
      render: (t) => (t.opportunity ? formatPipelineUsd(amountByOpp.get(t.opportunity) ?? 0) : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <StatusPill tone={toneForStatus(t.status)}>{statusLabel(t.status)}</StatusPill>,
    },
    { key: 'view', header: '', render: () => <ViewLink /> },
  ];

  const actionCols: Column<CampaignActionItem>[] = [
    { key: 'text', header: 'Item', render: (a) => a.text },
    { key: 'kind', header: 'Kind', render: (a) => (a.kind === 'followup' ? 'Follow-up' : 'Action item') },
    { key: 'owner', header: 'Owner', render: (a) => a.owner ?? '—' },
    { key: 'due', header: 'Due', render: (a) => fmtDate(a.due_date) },
    {
      key: 'status',
      header: 'Status',
      render: (a) => <StatusPill tone={toneForStatus(a.status)}>{actionItemStatusLabel(a.status)}</StatusPill>,
    },
  ];

  return (
    <>
      <div className={css.detailPad} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingLeft: 0, paddingRight: 0 }}>
        <span className={css.chip}>
          <StatusPill tone={toneForStatus(delivery.status)}>{deliveryStatusLabel(delivery.status)}</StatusPill>
        </span>
        {delivery.owner && <span className={css.chip}>Owner · {delivery.owner}</span>}
        <span className={css.chip}>Scheduled · {fmtDate(delivery.scheduled_for)}</span>
        <span className={css.chip}>Completed · {fmtDate(delivery.completed_at)}</span>
        <span className={css.chip}>{delivery.audience_mode === 'event' ? 'Event' : 'Single customer'}</span>
        <span className={css.chip}>Attributed · {formatPipelineUsd(pipelineAmount)}</span>
      </div>

      <HealthNote health={health} />

      <div className={css.grid}>
        {statusList.length > 0 && (
          <ChartCard
            title="Step status"
            chartType="donut_chart"
            data={stepStatusDistribution(statusList)}
            height={280}
            tileKey={`del-${delivery.id}-stepdist`}
          />
        )}
        <Panel title="Steps" count={statusList.length} flush full={statusList.length === 0}>
          <MasterTable
            columns={stepCols}
            rows={statusList}
            getRowId={(s) => s.id}
            empty="No step activity recorded yet."
          />
        </Panel>
      </div>

      <div className={css.split}>
        <Panel title="Customers & opportunities" subtitle="Click a customer to see its opportunity" count={targetList.length} flush>
          <MasterTable
            columns={targetCols}
            rows={targetList}
            getRowId={(t) => t.id}
            onRowClick={onSelectTarget}
            empty="No customer targets on this delivery."
          />
        </Panel>
        <Panel title="Action items & follow-ups" count={actionList.length} flush>
          <MasterTable
            columns={actionCols}
            rows={actionList}
            getRowId={(a) => a.id}
            empty="No action items captured."
          />
        </Panel>
      </div>
    </>
  );
};

export default DeliveryView;
